import {
  Asset,
  LiquidiumClient as LiquidiumSDK,
  type SendBtcTransactionRequest,
  type SendEthTransactionRequest,
  type SendIcrcTransferRequest,
  type SignMessageRequest,
  type WalletAdapter,
} from '@liquidium/client'
import { hashMessage } from 'viem'
import { config } from '../appConfig'
import {
  ethCallContract,
  sendBTC,
  transferEth,
  transferToken,
} from '../handlers/walletActions/wallet'
import { Chain, EVMChain, PlugToken } from '../interfaces/common'
import { logger } from '../logger'
import type { Wallet } from '../providers/WalletProvider'
import { GasCalculator } from '../utils/GasCalculator'
import { getEthProvider } from '../utils/getEthProvider'
import ChainFusionClient from './ChainFusionClient'
import OrdinalsClient from './OrdinalsClient'
import TatumClient from './TatumClient'

export const LIQUIDIUM_SIGNING_CHAIN = 'ETH' as const
export const LIQUIDIUM_ASSET_KEY = 'liquidium_asset'

const LIQUIDIUM_ASSETS: Asset[] = Object.values(Asset)

export interface LiquidiumAdapterContext {
  wallet?: Wallet
  evmChain?: EVMChain
}

class LiquidiumClient {
  private sdk: LiquidiumSDK
  private principalId?: string
  private context: LiquidiumAdapterContext

  constructor(principalId?: string, context: LiquidiumAdapterContext = {}) {
    this.principalId = principalId
    this.context = context
    this.sdk = new LiquidiumSDK({
      agent: config.getAgent(principalId ? { principalId } : undefined),
    })
  }

  get accounts() {
    return this.sdk.accounts
  }

  get market() {
    return this.sdk.market
  }

  get positions() {
    return this.sdk.positions
  }

  get lending() {
    return this.sdk.lending
  }

  get activities() {
    return this.sdk.activities
  }

  get history() {
    return this.sdk.history
  }

  get quote() {
    return this.sdk.quote
  }

  static assetOf(token: PlugToken): Asset | undefined {
    return LIQUIDIUM_ASSETS.find((asset) => asset === token.additionalData[LIQUIDIUM_ASSET_KEY])
  }

  getWalletAdapter(): WalletAdapter {
    return {
      sendIcrcTransfer: (request) => this.sendIcrcTransfer(request),
      sendBtcTransaction: (request) => this.sendBtcTransaction(request),
      sendEthTransaction: (request) => this.sendEthTransaction(request),
      signMessage: (request) => this.signMessage(request),
    }
  }

  private async sendIcrcTransfer({ transfer }: SendIcrcTransferRequest): Promise<string> {
    const blockIndex = await transferToken({
      tokenCanisterId: transfer.ledgerCanisterId,
      standard: 'ICRC1',
      amount: transfer.amount.toString(),
      receiver: {
        address: transfer.to.owner,
        subaccount: transfer.to.subaccount ? Array.from(transfer.to.subaccount) : undefined,
      },
      chain: Chain.ICP,
    })

    return String(blockIndex)
  }

  private async sendBtcTransaction({
    toAddress,
    amountSats,
  }: SendBtcTransactionRequest): Promise<string> {
    const btcAddress = this.context.wallet?.btcAddress
    if (!btcAddress) {
      throw Error('Native Bitcoin supply is unavailable: no Bitcoin address for this wallet')
    }
    if (amountSats === undefined) {
      throw Error('Native Bitcoin supply requires an amount')
    }

    // `send_btc` spends exactly the UTXOs it is handed and knows nothing about
    // inscriptions, so on a Taproot-only wallet the inputs must be picked around the
    // ordinals. Fetched here rather than passed in, so no caller can disable it.
    const [utxos, ordinals, feePerByte] = await Promise.all([
      TatumClient.getUTXOs(btcAddress, Chain.BTC),
      new OrdinalsClient().getOrdinals(btcAddress),
      TatumClient.getChainFee(Chain.BTC),
    ])

    const fee = TatumClient.estimateFee(
      utxos,
      ordinals,
      btcAddress,
      amountSats,
      feePerByte,
      Chain.BTC
    )

    const inputs = GasCalculator.utxosForAmount(utxos, ordinals, amountSats + fee)
    if (!inputs.length) {
      throw Error('Not enough spendable Bitcoin to cover the amount and network fee')
    }

    const txid = await sendBTC({
      to: toAddress,
      amount: amountSats.toString(),
      fee: fee.toString(),
      utxos: inputs,
    })

    return String(txid)
  }

  private async sendEthTransaction({ transaction }: SendEthTransactionRequest): Promise<string> {
    const { wallet, evmChain } = this.context
    if (!wallet?.ethAddress || !evmChain) {
      throw Error('Native Ethereum supply is unavailable: no Ethereum address or chain')
    }

    const value = BigInt(transaction.value ?? '0')
    const provider = getEthProvider(evmChain.rpcEndpoint)

    // Read per call, never cached: the SDK sends approve and deposit back to back, so
    // the second must see the nonce the first consumed.
    const [nonce, gas] = await Promise.all([
      provider.getTransactionCount({ address: wallet.ethAddress as `0x${string}` }),
      new GasCalculator(evmChain).getEthGas(wallet.ethAddress, value),
    ])

    if (!gas?.maxFeePerGas || gas.maxPriorityFeePerGas === undefined) {
      throw Error('Could not read Ethereum gas fees')
    }

    if (!transaction.data) {
      const hash = await transferEth({
        address: transaction.to,
        amount: value.toString(),
        gasLimit: gas.gasLimit?.toString(),
        maxFeePerGas: gas.maxFeePerGas.toString(),
        maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
        nonce,
        chain: evmChain,
      })

      return String(hash)
    }

    const gasLimit = await provider.estimateGas({
      account: wallet.ethAddress as `0x${string}`,
      to: transaction.to as `0x${string}`,
      data: transaction.data as `0x${string}`,
      value,
    })

    const hash = await ethCallContract({
      contractAddress: transaction.to,
      data: transaction.data,
      value: value.toString(),
      gasLimit: gasLimit.toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      nonce: nonce.toString(),
      chain: evmChain,
    })

    return String(hash)
  }

  private async signMessage({ message, chain }: SignMessageRequest): Promise<string> {
    if (chain !== LIQUIDIUM_SIGNING_CHAIN) {
      throw Error(`Plug cannot sign Liquidium messages for chain ${chain}`)
    }
    if (!this.principalId) {
      throw Error('Signing Liquidium messages requires an identity client')
    }

    const chainFusion = new ChainFusionClient(config.getAgent({ principalId: this.principalId }))
    const signature = await chainFusion.signEthHash(hashMessage(message)).catch((e) => {
      logger.error('Liquidium: failed to sign action message', { originalError: e })
      throw Error('Could not sign the Liquidium request')
    })

    if (!signature) throw Error('Could not sign the Liquidium request')

    return signature.startsWith('0x') ? signature : `0x${signature}`
  }

  // Effectively one-way: the protocol refuses to unlink a profile's last wallet, so
  // only call this for a deliberate user action.
  async createProfile(ethAddress: string): Promise<string> {
    const existing = await this.accounts.getProfileId(ethAddress)
    if (existing) return existing

    return this.accounts.createProfile({
      account: ethAddress,
      chain: LIQUIDIUM_SIGNING_CHAIN,
      walletAdapter: this.getWalletAdapter(),
    })
  }
}

export default LiquidiumClient
